import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { portalApi } from '../../api/portal.js';
import { filesApi } from '../../api/files.js';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { FileInput } from '../../components/common/FileInput.jsx';
import { FormField, Textarea } from '../../components/common/FormField.jsx';
import { formatDate } from '../../utils/dates.js';

export function SpeakerPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [submitted, setSubmitted] = useState({});
  const [bioText, setBioText] = useState('');
  const [bioNoteText, setBioNoteText] = useState('');
  const [noteText, setNoteText] = useState({});

  useEffect(() => {
    portalApi.get(token)
      .then((d) => { setData(d); setBioText(d.engagement.bio ?? ''); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const submitText = async (checklistKey, textValue) => {
    setSubmitting(checklistKey);
    try {
      await portalApi.submit(token, { checklistKey, textValue });
      setSubmitted((prev) => ({ ...prev, [checklistKey]: true }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(null);
    }
  };

  const submitFile = async (checklistKey, file) => {
    setSubmitting(checklistKey);
    try {
      const { id: fileId } = await filesApi.uploadAsPortal(token, file);
      await portalApi.submit(token, { checklistKey, fileId });
      setSubmitted((prev) => ({ ...prev, [checklistKey]: true }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(null);
    }
  };

  const updateChecklistNote = async (checklistKey, note) => {
    setSubmitting(checklistKey + '-note');
    try {
      await portalApi.updateChecklistItem(token, checklistKey, { note });
      setNoteText((prev) => ({ ...prev, [checklistKey]: '' }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <LoadingSpinner label="Loading your portal…" />
    </div>
  );

  if (error) return (
    <div style={{ maxWidth: 600, margin: '80px auto', padding: '0 var(--space-4)' }}>
      <div style={{ textAlign: 'center', marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-2)' }}>Speaker Portal</h1>
      </div>
      <ErrorAlert error={error} />
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 'var(--space-4)' }}>
        If you believe this is an error, please contact john@logisticalagility.com.
      </p>
    </div>
  );

  const { engagement, event, speakerFacingChecklist } = data;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gradient-page)', padding: 'var(--space-6) var(--space-4)' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
          <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-1)' }}>
            Speaker Portal
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {event?.name && <><strong>{event.name}</strong>{event.date && ` · ${formatDate(event.date)}`}</>}
          </p>
        </div>

        <ErrorAlert error={error} onDismiss={() => setError(null)} />

        {/* Biography */}
        <div className="card stack" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-semibold)' }}>Speaker biography</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Please review and update your biography for use in event promotion.
          </p>
          {submitted.biography ? (
            <p className="alert alert-success" style={{ fontSize: 'var(--text-sm)' }}>✓ Biography submitted — your coordinator will review it.</p>
          ) : (
            <>
              <FormField label="Biography" id="bio">
                <Textarea
                  id="bio"
                  value={bioText}
                  onChange={(e) => setBioText(e.target.value)}
                  rows={6}
                  placeholder="Your professional biography…"
                />
              </FormField>
              <button
                className="btn btn-primary"
                style={{ alignSelf: 'flex-start' }}
                disabled={submitting === 'biography' || !bioText.trim()}
                onClick={() => submitText('biography', bioText)}
              >
                {submitting === 'biography' ? 'Submitting…' : 'Submit biography'}
              </button>
            </>
          )}
        </div>

        {/* Headshot */}
        <div className="card stack" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-semibold)' }}>Headshot</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Upload a high-resolution headshot (JPG, PNG, or WebP). Recommended: at least 400×400px.
          </p>
          {submitted.headshot ? (
            <p className="alert alert-success" style={{ fontSize: 'var(--text-sm)' }}>✓ Headshot submitted — your coordinator will review it.</p>
          ) : (
            <FileInput
              onFile={(f) => submitFile('headshot', f)}
              accept="image/jpeg,image/png,image/webp"
              label={submitting === 'headshot' ? 'Uploading…' : 'Upload headshot'}
              hint="JPG, PNG, or WebP — drag and drop or click to browse"
            />
          )}
        </div>

        {/* Agreement */}
        <div className="card stack" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-semibold)' }}>Speaker agreement</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Upload your signed speaker agreement (PDF). Please sign and upload the agreement sent by your coordinator.
          </p>
          {submitted['agreement-rcvd'] ? (
            <p className="alert alert-success" style={{ fontSize: 'var(--text-sm)' }}>✓ Agreement submitted — your coordinator will review it.</p>
          ) : (
            <FileInput
              onFile={(f) => submitFile('agreement-rcvd', f)}
              accept="application/pdf"
              label={submitting === 'agreement-rcvd' ? 'Uploading…' : 'Upload signed agreement'}
              hint="PDF only"
            />
          )}
        </div>

        {/* Additional speaker-facing checklist items */}
        {speakerFacingChecklist?.filter((item) => !['biography', 'headshot', 'agreement-rcvd'].includes(item.key)).map((item) => (
          <div key={item.key} className="card stack" style={{ marginBottom: 'var(--space-4)' }}>
            <h2 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--weight-semibold)' }}>{item.label}</h2>
            {item.dueDate && (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Due: {formatDate(item.dueDate)}</p>
            )}
            {item.completed ? (
              <p className="alert alert-success" style={{ fontSize: 'var(--text-sm)' }}>✓ Complete</p>
            ) : (
              <>
                <FormField label="Note" id={`note-${item.key}`} hint="Add an optional note for your coordinator.">
                  <Textarea
                    id={`note-${item.key}`}
                    value={noteText[item.key] ?? ''}
                    onChange={(e) => setNoteText((prev) => ({ ...prev, [item.key]: e.target.value }))}
                    rows={2}
                  />
                </FormField>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ alignSelf: 'flex-start' }}
                  disabled={submitting === item.key + '-note'}
                  onClick={() => updateChecklistNote(item.key, noteText[item.key] ?? '')}
                >
                  {submitting === item.key + '-note' ? 'Saving…' : 'Save note'}
                </button>
              </>
            )}
          </div>
        ))}

        {/* Footer */}
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)', textAlign: 'center', marginTop: 'var(--space-8)' }}>
          Speaker Engagement Tracker · This link is private and scoped to your engagement.
        </p>
      </div>
    </div>
  );
}
